import { SERVICES_COLORS } from "./constants";

export type ServiceCategoryFilter =
  | "all"
  | "guestServices"
  | "activities"
  | "externalServices"
  | "guesteriaPartners";

export type ServiceUsageSegment = {
  key: string;
  name: string;
  value: number;
  fill: string;
};

export type ServiceAvailabilityDay =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export type TopServiceRow = {
  id: string;
  rate: number;
  name: string;
  imageUrl?: string;
  photoId?: string | null;
  category: Exclude<ServiceCategoryFilter, "all">;
  price?: number | string | null;
  bookings: number;
  guestsUsedPercent: number;
  revenue: number;
  availableFromTime?: string | null;
  availableToTime?: string | null;
  availabilityDays: ServiceAvailabilityDay[];
};

export const serviceUsageSegments: ServiceUsageSegment[] = [
  {
    key: "externalServices",
    name: "External Services",
    value: 620,
    fill: SERVICES_COLORS.externalServices,
  },
  {
    key: "guestServices",
    name: "Guest Services",
    value: 220,
    fill: SERVICES_COLORS.guestServices,
  },
  {
    key: "activities",
    name: "Activities",
    value: 158,
    fill: SERVICES_COLORS.activities,
  },
  {
    key: "guesteriaPartners",
    name: "Guesteria Partners",
    value: 42,
    fill: SERVICES_COLORS.guesteriaPartners,
  },
];

type TopServiceSeedRow = Omit<TopServiceRow, "availabilityDays">;

const allTopServicesBase: TopServiceSeedRow[] = [
  {
    id: "spa-massage",
    rate: 1,
    name: "SPA Massage 60 min",
    imageUrl:
      "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=64&h=64&fit=crop",
    category: "guestServices",
    bookings: 248,
    guestsUsedPercent: 82,
    revenue: 9420,
  },
  {
    id: "breakfast",
    rate: 2,
    name: "Breakfast",
    imageUrl:
      "https://images.unsplash.com/photo-1533089860892-a7c6f0a986b9?w=64&h=64&fit=crop",
    category: "guestServices",
    bookings: 186,
    guestsUsedPercent: 71,
    revenue: 7260,
  },
  {
    id: "airport-transfer",
    rate: 3,
    name: "Airport transfer",
    imageUrl:
      "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=64&h=64&fit=crop",
    category: "externalServices",
    bookings: 142,
    guestsUsedPercent: 52,
    revenue: 5890,
  },
  {
    id: "late-checkout",
    rate: 4,
    name: "Late checkout",
    category: "guestServices",
    bookings: 98,
    guestsUsedPercent: 44,
    revenue: 4120,
  },
  {
    id: "city-tour",
    rate: 5,
    name: "City tour",
    imageUrl:
      "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=64&h=64&fit=crop",
    category: "activities",
    bookings: 87,
    guestsUsedPercent: 32,
    revenue: 1420,
  },
  {
    id: "room-service-dinner",
    rate: 6,
    name: "Room service dinner",
    imageUrl:
      "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=64&h=64&fit=crop",
    category: "guestServices",
    bookings: 76,
    guestsUsedPercent: 28,
    revenue: 3840,
  },
  {
    id: "yoga-class",
    rate: 7,
    name: "Morning yoga class",
    imageUrl:
      "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=64&h=64&fit=crop",
    category: "activities",
    bookings: 64,
    guestsUsedPercent: 24,
    revenue: 1280,
  },
  {
    id: "laundry-express",
    rate: 8,
    name: "Express laundry",
    category: "guestServices",
    bookings: 58,
    guestsUsedPercent: 21,
    revenue: 1160,
  },
  {
    id: "wine-tasting",
    rate: 9,
    name: "Wine tasting",
    imageUrl:
      "https://images.unsplash.com/photo-1510812431401-41d2bd2724f3?w=64&h=64&fit=crop",
    category: "activities",
    bookings: 52,
    guestsUsedPercent: 19,
    revenue: 3120,
  },
  {
    id: "car-rental",
    rate: 10,
    name: "Car rental",
    imageUrl:
      "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=64&h=64&fit=crop",
    category: "externalServices",
    bookings: 48,
    guestsUsedPercent: 18,
    revenue: 4800,
  },
  {
    id: "babysitting",
    rate: 11,
    name: "Babysitting",
    category: "guestServices",
    bookings: 41,
    guestsUsedPercent: 15,
    revenue: 2050,
  },
  {
    id: "museum-tickets",
    rate: 12,
    name: "Museum tickets",
    imageUrl:
      "https://images.unsplash.com/photo-1566127444979-b3b2aee2d16b?w=64&h=64&fit=crop",
    category: "guesteriaPartners",
    bookings: 39,
    guestsUsedPercent: 14,
    revenue: 1170,
  },
  {
    id: "spa-facial",
    rate: 13,
    name: "SPA Facial 45 min",
    imageUrl:
      "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=64&h=64&fit=crop",
    category: "guestServices",
    bookings: 36,
    guestsUsedPercent: 13,
    revenue: 2880,
  },
  {
    id: "boat-trip",
    rate: 14,
    name: "Boat trip",
    imageUrl:
      "https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=64&h=64&fit=crop",
    category: "guesteriaPartners",
    bookings: 34,
    guestsUsedPercent: 12,
    revenue: 3400,
  },
  {
    id: "parking-valet",
    rate: 15,
    name: "Valet parking",
    category: "externalServices",
    bookings: 31,
    guestsUsedPercent: 11,
    revenue: 930,
  },
  {
    id: "minibar-refill",
    rate: 16,
    name: "Minibar refill",
    category: "guestServices",
    bookings: 29,
    guestsUsedPercent: 10,
    revenue: 870,
  },
  {
    id: "cooking-class",
    rate: 17,
    name: "Cooking class",
    imageUrl:
      "https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=64&h=64&fit=crop",
    category: "activities",
    bookings: 27,
    guestsUsedPercent: 9,
    revenue: 2160,
  },
  {
    id: "helicopter-tour",
    rate: 18,
    name: "Helicopter tour",
    imageUrl:
      "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=64&h=64&fit=crop",
    category: "guesteriaPartners",
    bookings: 24,
    guestsUsedPercent: 8,
    revenue: 9600,
  },
  {
    id: "pet-friendly-kit",
    rate: 19,
    name: "Pet-friendly kit",
    category: "guestServices",
    bookings: 22,
    guestsUsedPercent: 7,
    revenue: 660,
  },
  {
    id: "bike-rental",
    rate: 20,
    name: "Bike rental",
    imageUrl:
      "https://images.unsplash.com/photo-1485965120188-e96f0561137d?w=64&h=64&fit=crop",
    category: "externalServices",
    bookings: 20,
    guestsUsedPercent: 6,
    revenue: 400,
  },
  {
    id: "concert-tickets",
    rate: 21,
    name: "Concert tickets",
    category: "guesteriaPartners",
    bookings: 18,
    guestsUsedPercent: 5,
    revenue: 2700,
  },
  {
    id: "gym-pass",
    rate: 22,
    name: "Gym day pass",
    imageUrl:
      "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=64&h=64&fit=crop",
    category: "guestServices",
    bookings: 16,
    guestsUsedPercent: 4,
    revenue: 320,
  },
  {
    id: "hiking-guide",
    rate: 23,
    name: "Hiking with guide",
    imageUrl:
      "https://images.unsplash.com/photo-1551632811-534143875610?w=64&h=64&fit=crop",
    category: "activities",
    bookings: 14,
    guestsUsedPercent: 3,
    revenue: 1120,
  },
  {
    id: "flower-bouquet",
    rate: 24,
    name: "Flower bouquet",
    category: "guestServices",
    bookings: 12,
    guestsUsedPercent: 2,
    revenue: 480,
  },
  {
    id: "theatre-tickets",
    rate: 25,
    name: "Theatre tickets",
    category: "guesteriaPartners",
    bookings: 10,
    guestsUsedPercent: 1,
    revenue: 1500,
  },
];

const SERVICE_AVAILABILITY_ROTATION: ServiceAvailabilityDay[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

function buildAvailabilityDays(
  row: TopServiceSeedRow,
  index: number,
): ServiceAvailabilityDay[] {
  if (row.category === "activities") {
    return ["friday", "saturday", "sunday"];
  }

  if (row.category === "guesteriaPartners") {
    return ["thursday", "friday", "saturday", "sunday"];
  }

  if (row.category === "externalServices") {
    return ["monday", "tuesday", "wednesday", "thursday", "friday"];
  }

  const offset = index % SERVICE_AVAILABILITY_ROTATION.length;
  return [
    SERVICE_AVAILABILITY_ROTATION[offset],
    SERVICE_AVAILABILITY_ROTATION[(offset + 1) % 7],
    SERVICE_AVAILABILITY_ROTATION[(offset + 2) % 7],
    SERVICE_AVAILABILITY_ROTATION[(offset + 3) % 7],
    SERVICE_AVAILABILITY_ROTATION[(offset + 4) % 7],
  ];
}

export const allTopServicesMock: TopServiceRow[] = allTopServicesBase.map(
  (row, index) => ({
    ...row,
    availabilityDays: buildAvailabilityDays(row, index),
  }),
);

export const topServicesMock = allTopServicesMock.slice(0, 5);

export const SERVICE_USAGE_TOTAL = serviceUsageSegments.reduce(
  (sum, segment) => sum + segment.value,
  0,
);

export function filterTopServices(
  rows: TopServiceRow[],
  category: ServiceCategoryFilter,
): TopServiceRow[] {
  if (category === "all") {
    return rows;
  }

  return rows.filter((row) => row.category === category);
}

export function filterUsageSegments(
  segments: ServiceUsageSegment[],
  category: ServiceCategoryFilter,
): ServiceUsageSegment[] {
  if (category === "all") {
    return segments;
  }

  return segments.filter((segment) => segment.key === category);
}

export const TOP_SERVICES_LIST_PAGE_SIZE = 7;
